'use client';
import React, { useRef, useEffect } from 'react';
import PostItem from './post-item';
import type { MessageImageLayoutWidths } from '@ui/hooks/file/message-image-layout';
import { PostItemSkeleton } from './post-item-skeleton';
import { EmptyScreen } from '@ui/components/moleculas/empty-screen';
import { Button } from '@ui/components/atoms/button';
import { ContextUserType } from '@ui/constants/context';
import { IMessageState } from '@ui/components/organisms/message/message.typings';
import type { IMessage } from '@ui/components/organisms/message/message.typings';
import { useThreadPaginationSentinel } from '@ui/hooks/use-thread-pagination-sentinel';

interface PostListSegmentProps {
  imageLayoutWidths?: MessageImageLayoutWidths;
  isLoading: boolean;
  initialLoading: boolean;
  isActive?: boolean;
  messageState: IMessageState;
  userType: ContextUserType;
  isDynamicRoute?: boolean;
  emptyState: {
    title: string;
    message: string;
    actionButton?: {
      label: string;
      onClick: () => void;
    };
  };
  showEmptyScreen: boolean;
  nextPage: () => void;
  moderators_ids: string[];
  attributedTargetPost?: IMessage;
  attributedTargetPostId?: string;
  onAttributedTargetViewed?: (resourceId: string) => void | Promise<void>;
}

const MemoizedPostItem = React.memo(PostItem);

const PostListSegment = (props: PostListSegmentProps) => {
  const {
    attributedTargetPostId,
    initialLoading,
    isActive = true,
    isLoading,
    onAttributedTargetViewed,
  } = props;
  const isFetching = props.messageState.isFetching;
  const attributedTargetRef = useRef<HTMLDivElement | null>(null);
  const completedAttributedTargetRef = useRef<string>();
  const query = props.messageState.query;
  const isAccessError = Boolean(
    props.messageState.isMessagesError &&
      [401, 403, 404, 422].includes(Number(props.messageState.messagesError?.status)),
  );
  const pinnedMessages = isAccessError ? [] : props.messageState.pinned_messages || [];
  const messages = isAccessError ? [] : props.messageState.messages;
  const attributedTargetPost = isAccessError ? undefined : props.attributedTargetPost;
  const hasUsableContent = Boolean(
    messages.length || pinnedMessages.length || attributedTargetPost,
  );
  const targetPostAlreadyRendered = [...pinnedMessages, ...messages].some(
    (post) => post.id === props.attributedTargetPostId,
  );
  const renderedMessages =
    attributedTargetPost && !targetPostAlreadyRendered
      ? [attributedTargetPost, ...messages]
      : messages;
  const firstPostWithImage = [...pinnedMessages, ...renderedMessages].find((post) =>
    Boolean(post.files?.images?.length),
  );
  const paginationProgressKey = [
    query.thread_id,
    query.creator_id,
    query.conversation_id,
    query.conversation_type,
    query.fan_id,
    query.created_by_creator,
    query.draft,
    query.ordering,
    query.pageSize,
    query.message_ids,
    query.anchor_id,
    isActive,
  ].join('|');
  const paginationSentinelRef = useThreadPaginationSentinel({
    enabled: isActive && props.messageState.hasMorePages && !props.messageState.isMessagesError,
    isFetching: props.messageState.isFetching,
    onLoadMore: props.nextPage,
    resetKey: `${query.page ?? props.messageState.maxLoadedPage ?? 1}:${messages.length}`,
    progressKey: paginationProgressKey,
    requireExitBeforeNextLoad: true,
    rootMargin: '1000px 0px',
  });

  useEffect(() => {
    const targetPostId = attributedTargetPostId;
    if (
      !targetPostId ||
      !attributedTargetRef.current ||
      completedAttributedTargetRef.current === targetPostId ||
      isLoading ||
      initialLoading ||
      isFetching
    ) {
      return;
    }
    const animationFrame = window.requestAnimationFrame(() => {
      attributedTargetRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      completedAttributedTargetRef.current = targetPostId;
      void onAttributedTargetViewed?.(targetPostId);
    });
    return () => window.cancelAnimationFrame(animationFrame);
  }, [attributedTargetPostId, initialLoading, isFetching, isLoading, onAttributedTargetViewed]);

  const renderPinnedMessages = () => {
    if (props.isLoading || props.initialLoading || !pinnedMessages.length) {
      return null;
    }

    return (
      <div className="mb-2">
        <div className="space-y-2 w-full">
          {pinnedMessages.map((post) => (
            <div
              key={post.id}
              ref={post.id === attributedTargetPostId ? attributedTargetRef : undefined}
              className="min-w-[96%] flex-grow h-fit">
              <MemoizedPostItem
                key={post.id}
                post={post}
                userType={props.userType}
                userId={props.messageState.user_id}
                creator_id={post.creator_id}
                communityPostRoute={props.messageState.communityPostRoute}
                query={props.messageState.query}
                contentType="post"
                isDynamicRoute={props.isDynamicRoute}
                communityDetails={props.messageState.community_details}
                moderators_ids={props.moderators_ids}
                attachmentLoading={post.id === firstPostWithImage?.id ? 'eager' : 'lazy'}
                attachmentImageLayoutWidths={props.imageLayoutWidths}
                reserveAttachmentSpace
              />
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderLoadingState = () => {
    if (
      hasUsableContent ||
      props.messageState.isMessagesError ||
      (!props.isLoading && !props.initialLoading)
    ) {
      return null;
    }

    return (
      <div className="p-3 md:p-0">
        <PostItemSkeleton />
      </div>
    );
  };

  const renderMessages = () => {
    if (!renderedMessages.length) {
      return null;
    }

    return (
      <div className="pb-10 space-y-2" key={props.messageState.query.ordering}>
        {renderedMessages.map((post) => (
          <div
            key={post.id}
            ref={post.id === attributedTargetPostId ? attributedTargetRef : undefined}>
            <MemoizedPostItem
              post={post}
              userType={props.userType}
              userId={props.messageState.user_id}
              creator_id={post.creator_id}
              communityPostRoute={props.messageState.communityPostRoute}
              query={props.messageState.query}
              contentType="post"
              isDynamicRoute={props.isDynamicRoute}
              communityDetails={props.messageState.community_details}
              moderators_ids={props.moderators_ids}
              attachmentLoading={post.id === firstPostWithImage?.id ? 'eager' : 'lazy'}
              attachmentImageLayoutWidths={props.imageLayoutWidths}
              reserveAttachmentSpace
            />
          </div>
        ))}

        {props.messageState.hasMorePages && !props.messageState.isMessagesError && (
          <div ref={paginationSentinelRef} aria-hidden="true" className="min-h-px" />
        )}
        {props.messageState.isFetching && props.messageState.hasMorePages && (
          <div className="my-2" aria-live="polite">
            <PostItemSkeleton />
          </div>
        )}
        {props.messageState.hasMorePages &&
          !props.messageState.isMessagesError &&
          !props.messageState.isFetching && (
            <div className="flex justify-center py-4">
              <Button type="secondary" size="small" behavior="button" onClick={props.nextPage}>
                Load more posts
              </Button>
            </div>
          )}
      </div>
    );
  };

  const renderRetry = () => (
    <div className="flex flex-col items-center justify-center gap-2 py-4" role="alert">
      <span className="text-sm text-korDarkGrey">Unable to load posts.</span>
      <Button
        type="secondary"
        size="small"
        behavior="button"
        onClick={() => void props.messageState.refetchMessages()}
        disabled={props.messageState.isFetching}>
        Try again
      </Button>
    </div>
  );

  const renderEmptyState = () => {
    if (
      props.messageState.isMessagesError ||
      isAccessError ||
      !props.showEmptyScreen ||
      props.initialLoading ||
      pinnedMessages.length
    ) {
      return null;
    }

    const { actionButton, ...emptyStateProps } = props.emptyState;

    return (
      <EmptyScreen
        {...emptyStateProps}
        button={actionButton?.label}
        onClick={actionButton?.onClick}
        className="mx-auto h-fit w-full max-w-sm bg-transparent lg:bg-transparent pb-10"
      />
    );
  };

  return (
    <>
      {renderPinnedMessages()}
      {renderLoadingState()}
      {renderMessages()}
      {props.messageState.isMessagesError && !isAccessError && renderRetry()}
      {renderEmptyState()}
    </>
  );
};

export default PostListSegment;
